/**
 * MegaMenu Context
 *
 * Manages open/close state for the mega menu overlay.
 * Provides keyboard shortcut (Ctrl+M) to toggle.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface MegaMenuContextType {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const MegaMenuContext = createContext<MegaMenuContextType | null>(null);

export const useMegaMenu = (): MegaMenuContextType => {
  const context = useContext(MegaMenuContext);
  if (!context) {
    throw new Error('useMegaMenu must be used within a MegaMenuProvider');
  }
  return context;
};

/**
 * Safe hook that returns null if used outside provider
 */
export const useMegaMenuSafe = (): MegaMenuContextType | null => {
  return useContext(MegaMenuContext);
};

interface MegaMenuProviderProps {
  children: React.ReactNode;
}

export const MegaMenuProvider: React.FC<MegaMenuProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // Keyboard shortcut: Ctrl+M or Cmd+M to toggle
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+M or Cmd+M
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'm') {
        event.preventDefault();
        toggle();
      }
      // Escape to close
      if (event.key === 'Escape' && isOpen) {
        close();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, toggle, close]);

  return (
    <MegaMenuContext.Provider value={{ isOpen, open, close, toggle }}>
      {children}
    </MegaMenuContext.Provider>
  );
};

export default MegaMenuContext;
